// utils
import { study_identification, study_eligibilty, study_scope } from "../../dataSchemas/protocol/fieldSchemas"
import { getWidget } from "../shared/utils"
import { getPreviewStyles } from "./utils/utils"
import { setHiddenFields } from "./utils/utils"
import { hideStudyDetailsKeys } from "./utils/utils"

import { protocolClassNames, firstProtocolSectionClassNames, objectClassNames, arrayClassNames } from "../classes"

import { ctgProtocolUISchema } from "./ctgProtocolUISchema"

const ctgSchema = ctgProtocolUISchema()

const hideFields = {
  root: [
    ...hideStudyDetailsKeys.root,
    "protocol_id",
    "ctis_form",
    "mscs",
    // "eudract_number",
    "study_reference",
    "study_arms",
    "study_outcome_measures",
    "contact_information",
    "study_oversight",
    "ema_hma_additional_information",
    "emarwd_official_title_and_acronym",
    "milestone_dates",
    "secondary_attributes",
  ],
  study_identification: {
    root: [
      "eupas_registry_number",
      "darwin_eu_study",
      "study_topic",
      "country_regulator_not_listed",
      "study_topic_other",
      "other_study_ids",
      "regulatory_procedure_number",
    ],
  },
  study_timeline: {
    root: [
      "record_verification_date",
      "milestone_dates",
      "signed_funding_contract_date",
      // "data_collection_start_date",
      "data_analysis_start_date",
      "interim_report_date",
      "final_study_report_date",
      "other_funding_sources",
      "more_details_sources_of_funding",
      "expanded",
    ],
    recruitment: ["study_stopped"],
  },
  study_ipd: {
    root: ["ipd_sharing_info", "time_frame", "url_text", "access_criteria"],
  },
  study_description: {
    root: [
      "detailed_description",
      "additional_medical_conditions",
      "trial_scope",
      "ctis_trial_phase",
      "brief_summary_translations",
      "secondary_objective",
      "secondary_objective_translations",
      "low_intervention_trial",
      "therapeutic_area",
      "meddra",
      "principle_inclusion_criteria",
      "principle_exlusion_criteria",
      "protocol_information",
      "scientific_advice_and_pip",
      "associated_clinical_trials",
      "sponsors",
      "products",
    ],
    medical_conditions: ["translations", "is_medical_condition_rare"],
  },
  study_eligibilty: {
    root: [
      "registered_data_sources",
      "other_registered_data_sources",
      "short_description_of_study_population",
      "age_groups",
      "special_interest_of_population",
      "other_special_interest_of_population",
    ],
  },
  study_scope: {
    root: [
      "clinical_trial_regulatory_scope",
      "clinical_trial_type",
      "data_collection_method",
      "other_non_interventional_study_design",
      "additional_patient_registry_data",
      "follow_up_duration",
      "duration_type",
    ],
  },
  milestone_dates: {
    root: [...hideStudyDetailsKeys.milestone_dates.root, "pcd", "fpi"],
  },
}

const showFields = {
  root: ["type_of_research"],
  study_identification: [
    "category_of_study",
    "subcategory_of_study",
    "jrct_number",
    "official_title",
    "acronym",
    "brief_title",
    "public_title_acronym",
  ],
  study_eligibilty: ["age_limits", "sex"],
  study_scope: [
    "study_phase",
    "enrollment_number",
    "allocation",
    "control",
    "interventional_study_model",
    "primary_purpose",
    "placebo",
    "blinding",
    "randomization",
    "non_insurance_combined_medical_expenses",
    "countries_of_recruitment_except_japan",
  ],
}

export const jrctProtocolUISchema = (configs) => {
  let preview = configs?.preview
  let formData = configs?.formData
  const disableValidation = configs?.disableValidation || false

  let returnObj = {
    "ui:options": {
      classNames: {
        objectContainer: "rjs-full",
        field: "rjs-full",
      },
    },
    protocol: {
      "ui:order": [
        "type_of_research",
        "study_type",
        "study_identification",
        "investigator_information",
        "non_investigator_information",
        "information_on_multicenter_clinical_trials",
        "milestone_dates",
        "study_description",
        "study_eligibilty",
        "study_scope",
        "overview_of_pharmaceuticals",
        "study_timeline",
        "monetary_sponsor",
        "irb_information",
        "study_ipd",
        "*",
      ],
      "ui:options": {
        title: "Research Information",
        translations: {
          jp: {
            title: "研究の情報",
          },
        },
        classNames: protocolClassNames,
      },
      type_of_research: {
        "ui:options": {
          title: "Type of research",
          translations: {
            jp: {
              title: "研究の種類",
            },
          },
          label: false,
          required: true,
          disableValidation,
          classNames: {
            field: "rjs-col-lg",
          },
        },
      },
      study_type: {
        "ui:options": {
          title: "Study Type",
          required: true,
          disableValidation,
          label: false,
          translations: {
            jp: {
              title: "試験等の種類",
            },
          },
          classNames: {
            field: "rjs-col-lg",
          },
        },
      },
      ...setHiddenFields(hideFields.root),
      study_identification: {
        "ui:order": [
          "category_of_study",
          "subcategory_of_study",
          "jrct_number",
          "official_title",
          "acronym",
          "brief_title",
          "public_title_acronym",
          "*",
        ],
        "ui:options": {
          classNames: firstProtocolSectionClassNames,
        },
        category_of_study: {
          "ui:options": {
            label: false,
            title: "Category of study",
            translations: {
              jp: {
                title: "試験等の種別",
              },
            },
            required: true,
            disableValidation,
            classNames: {
              field: "rjs-col-lg",
            },
          },
        },
        subcategory_of_study: {
          "ui:options": {
            label: false,
            required: true,
            disableValidation,
            title: "Sub-category of study",
            translations: {
              jp: {
                title: "治験の区分",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        jrct_number: {
          "ui:options": {
            title: "jRCT number",
            label: false,
            tooltip: "If there is a clinical trial notification reception number, please enter it.",
            translations: {
              jp: {
                title: "jRCT番号",
                tooltip: "治験届の受付番号等があれば記載すること",
              },
            },
            classNames: {
              field: "rjs-col-lg",
            },
          },
        },
        official_title: {
          "ui:options": {
            label: false,
            title: "Scientific Title",
            placeholder: "Enter Scientific Title",
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 2000,
            translations: {
              jp: {
                title: "試験等の名称",
                maxChars: 1200,
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        acronym: {
          "ui:options": {
            title: "Scientific Title (Acronym)",
            label: false,
            disableValidation,
            charCountEnabled: true,
            maxChars: 255,
            tooltip: "If you have an abbreviation, please enter it.",
            translations: {
              jp: {
                title: "学名(学術タイトル)(頭字語)",
                tooltip: "略称があれば入力してください。",
                charCountEnabled: false,
              },
            },
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        brief_title: {
          "ui:options": {
            label: false,
            title: "Public Title",
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 2000,
            translations: {
              jp: {
                title: "平易な試験等の名称",
                maxChars: 1200,
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        public_title_acronym: {
          "ui:options": {
            title: "Public Title (Acronym)",
            label: false,
            disableValidation,
            tooltip: "If you have an abbreviation, please enter it.",
            charCountEnabled: true,
            maxChars: 255,
            translations: {
              jp: {
                title: "公開タイトル(頭字語)",
                tooltip: "略称があれば入力してください。",
                charCountEnabled: false,
              },
            },
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        registered_in_jrct_registry: {
          "ui:options": {
            label: false,
            title: "Registered on jRCT?",
            // hideReviewer: true,
            translations: {
              jp: {
                title: "jRCTに登録されていますか?",
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        summary_of_changes: {
          "ui:options": {
            title: "Summary of changes",
            classNames: objectClassNames.tertiary,
            translations: {
              jp: {
                title: "変更の概要",
              },
            },
          },
          study_info_after_change: {
            "ui:options": {
              label: false,
              title: "Study Information after the change",
              classNames: {
                objectTitle: "rjs-h3",
                fieldContainer: "rjs-full",
              },
              translations: {
                jp: {
                  title: "変更後の学習情報",
                },
              },
            },
            "ui:widget": getWidget({ type: "textarea", preview }),
          },
          study_info_before_change: {
            "ui:options": {
              label: false,
              title: "Study Information before the change",
              classNames: {
                objectTitle: "rjs-h3",
                fieldContainer: "rjs-full",
              },
              translations: {
                jp: {
                  title: "変更前の学習情報",
                },
              },
            },
            "ui:widget": getWidget({ type: "textarea", preview }),
          },
        },
        ...setHiddenFields(
          [...Object.keys(study_identification.properties), ...hideFields.study_identification.root],
          showFields.study_identification
        ),
      },
      investigator_information: {
        "ui:options": {
          title: "Principal Investigator Information",
          translations: {
            jp: {
              title: "研究責任医師の情報",
            },
          },
          classNames: objectClassNames.primary,
        },
        "ui:order": ["*", "facilities_equipment_necessary_for_emergency_medical_care"],
        contact_for_scientific_queries: {
          "ui:options": {
            title: "Contact for Scientific Queries",
            translations: {
              jp: {
                title: "科学的な内容の問合せ先",
              },
            },
            classNames: objectClassNames.contact_root_container,
          },
          full_name: {
            "ui:options": {
              label: false,
              title: "Name",
              translations: {
                jp: {
                  title: "氏名",
                },
              },
              classNames: objectClassNames.contact_nested_container,
            },
            surname: {
              "ui:options": {
                label: false,
                title: "Last",
                charCountEnabled: true,
                maxChars: 50,
                translations: {
                  jp: {
                    title: "名字",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            given_name: {
              "ui:options": {
                label: false,
                title: "First",
                charCountEnabled: true,
                maxChars: 50,
                translations: {
                  jp: {
                    title: "名前",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
          },
          erad_number: {
            "ui:options": {
              label: false,
              title: "e-Rad Number",
              charCountEnabled: true,
              maxChars: 10,
              translations: {
                jp: {
                  title: "e-Rad番号",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-md",
              },
            },
          },
          affiliation: {
            "ui:options": {
              label: false,
              title: "Affiliation",
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 255,
              translations: {
                jp: {
                  title: "所属期間（実施医療機関）",
                  maxChars: 200,
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          department: {
            "ui:options": {
              label: false,
              title: "Department",
              charCountEnabled: true,
              maxChars: 100,
              translations: {
                jp: {
                  title: "所属部署",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          postal_code: {
            "ui:options": {
              label: false,
              title: "Zip code",
              tooltip: "Please enter numbers and half-width hyphens",
              charCountEnabled: true,
              maxChars: 8,
              translations: {
                jp: {
                  title: "所属機関の郵便番号",
                  tooltip: "/数値・半角ハイフンで入力してください",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-md",
              },
            },
          },
          prefecture: {
            "ui:options": {
              label: false,
              title: "Prefecture",
              translations: {
                jp: {
                  title: "所属機関の所在都道府県",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          address: {
            "ui:options": {
              label: false,
              title: "Address",
              tooltip: "Please enter including the prefecture",
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 200,
              translations: {
                jp: {
                  title: "所属機関の住所",
                  tooltip: "市区町村以降を入力してください。例）さいたま市、千代田区",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
          },
          phone: {
            "ui:options": {
              label: false,
              title: "Phone",
              tooltip: "Please enter numbers and half-width hyphens",
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 40,
              translations: {
                jp: {
                  title: "電話番号",
                  tooltip: "数値・半角ハイフンで入力してください",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-md",
                field: "rjs-col-md",
              },
            },
          },
          email: {
            "ui:options": {
              label: false,
              title: "Email",
              tooltip: "Please enter in email address format",
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 255,
              translations: {
                jp: {
                  title: "電子メールアドレス",
                  tooltip: "メールアドレス形式で入力してください",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-md",
              },
            },
          },
        },
        contact_for_public_queries: {
          "ui:options": {
            title: "Contact for Public Queries",
            translations: {
              jp: {
                title: "試験に関する問い合わせ先",
              },
            },
            classNames: objectClassNames.contact_root_container,
          },
          full_name: {
            "ui:options": {
              label: false,
              title: "Name",
              translations: {
                jp: {
                  title: "氏名",
                },
              },
              classNames: objectClassNames.contact_nested_container,
            },
            surname: {
              "ui:options": {
                label: false,
                title: "Last",
                charCountEnabled: true,
                maxChars: 50,
                translations: {
                  jp: {
                    title: "名字",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            given_name: {
              "ui:options": {
                label: false,
                title: "First",
                charCountEnabled: true,
                maxChars: 50,
                translations: {
                  jp: {
                    title: "名前",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
          },
          affiliation: {
            "ui:options": {
              label: false,
              title: "Affiliation",
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 255,
              translations: {
                jp: {
                  title: "担当者所属機関",
                  maxChars: 200,
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          department: {
            "ui:options": {
              label: false,
              title: "Department",
              charCountEnabled: true,
              maxChars: 100,
              translations: {
                jp: {
                  title: "担当者所属部署",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          postal_code: {
            "ui:options": {
              label: false,
              title: "Zip code",
              tooltip: "Please enter numbers and half-width hyphens",
              charCountEnabled: true,
              maxChars: 8,
              translations: {
                jp: {
                  title: "担当者所属機関の郵便番号",
                  tooltip: "数値・半角ハイフンで入力してください",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-md",
              },
            },
          },
          prefecture: {
            "ui:options": {
              label: false,
              title: "Prefecture",
              translations: {
                jp: {
                  title: "所属機関の所在都道府県",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          address: {
            "ui:options": {
              label: false,
              title: "Address",
              tooltip: "Please enter including the prefecture",
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 200,
              translations: {
                jp: {
                  title: "担当者所属機関の住所",
                  tooltip: "市区町村以降を入力してください。例）さいたま市、千代田区",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-xl",
              },
            },
          },
          phone: {
            "ui:options": {
              label: false,
              title: "Phone",
              tooltip: "Please enter numbers and half-width hyphens",
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 40,
              translations: {
                jp: {
                  title: "電話番号",
                  tooltip: "数値・半角ハイフンで入力してください",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-md",
                field: "rjs-col-md",
              },
            },
          },
          fax: {
            "ui:options": {
              label: false,
              title: "Fax Number",
              tooltip: "Please enter numbers and half-width hyphens",
              charCountEnabled: true,
              maxChars: 40,
              translations: {
                jp: {
                  title: "FAX番号",
                  tooltip: "数値・半角ハイフンで入力してください",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-md",
                field: "rjs-col-md",
              },
            },
          },
          email: {
            "ui:options": {
              label: false,
              title: "Email",
              tooltip: "Please enter in email address format",
              required: true,
              disableValidation,
              charCountEnabled: true,
              maxChars: 255,
              translations: {
                jp: {
                  title: "電子メールアドレス",
                  tooltip: "メールアドレス形式で入力してください",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-md",
              },
            },
          },
        },
        medical_institution_name: {
          "ui:options": {
            label: false,
            title: "Head of the medical institution",
            classNames: objectClassNames.tertiary,
            translations: {
              jp: {
                title: "実施医療機関の長の氏名",
              },
            },
          },
          surname: {
            "ui:options": {
              label: false,
              title: "Last name",
              charCountEnabled: true,
              maxChars: 50,
              translations: {
                jp: {
                  title: "名字",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          given_name: {
            "ui:options": {
              label: false,
              title: "First name",
              charCountEnabled: true,
              maxChars: 50,
              translations: {
                jp: {
                  title: "名前",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
        },
        administrator_permission: {
          "ui:options": {
            label: false,
            title: "Does the administrator have permission for the relevant examination?",
            translations: {
              jp: {
                title: "当該試験等に対する管理者の許可の有無",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-2",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        approval_date: {
          "ui:options": {
            label: false,
            title: "Approval Date of First IRB",
            tooltip: "Please enter the date on which the implementation of the relevant test was approved",
            translations: {
              jp: {
                title: "IRBの承認日",
                tooltip: "当該試験等の実施が承認された日を入力してください",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-2",
              field: "rjs-col-date",
            },
          },
          "ui:widget": getWidget({ type: "date", preview }),
        },
        facilities_equipment_necessary_for_emergency_medical_care: {
          "ui:options": {
            label: false,
            title: "Facilities for emergency medical care",
            translations: {
              jp: {
                title: "救急医療に必要な施設又は設備",
              },
            },
            disableValidation,
            charCountEnabled: true,
            maxChars: 250,
            classNames: {
              fieldContainer: "rjs-col-1-2",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
      },
      non_investigator_information: {
        "ui:options": {
          title: "Other Contributors Information",
          translations: {
            jp: {
              title: "その他の試験等に従事する者に関する事項",
            },
          },
          classNames: objectClassNames.primary,
        },
        data_management_agency_contacts: {
          "ui:options": {
            title: "Data Management Agencies",
            itemTitle: "Data Management Agency",
            addButtonTitle: "Add Data Management Agency",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                addButtonTitle: "データマネジメント担当機関を追加",
              },
            },
          },
          items: {
            organization_in_charge_of_data_management: {
              "ui:options": {
                label: false,
                title: "Responsible Organization for Data Management",
                translations: {
                  jp: {
                    title: "データマネジメント担当機関",
                  },
                },
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                classNames: {
                  fieldContainer: "rjs-col-1-2",
                },
              },
            },
            head_of_data_management: {
              "ui:options": {
                title: "Responsible Person for Data Management",
                translations: {
                  jp: {
                    title: "データマネジメント担当責任者",
                  },
                },
                classNames: objectClassNames.contact_root_container,
              },
              full_name: {
                "ui:options": {
                  label: false,
                  title: "Name",
                  translations: {
                    jp: {
                      title: "氏名",
                    },
                  },
                  classNames: objectClassNames.contact_nested_container,
                },
                surname: {
                  "ui:options": {
                    label: false,
                    title: "Last",
                    charCountEnabled: true,
                    maxChars: 40,
                    disableValidation,
                    translations: {
                      jp: {
                        title: "名字",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                given_name: {
                  "ui:options": {
                    label: false,
                    title: "First",
                    charCountEnabled: true,
                    disableValidation,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名前",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
              },
              erad_number: {
                "ui:options": {
                  label: false,
                  title: "e-Rad Number",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 10,
                  translations: {
                    jp: {
                      title: "e-Rad番号",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
              affiliation: {
                "ui:options": {
                  label: false,
                  title: "Affiliation",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "所属",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              post: {
                "ui:options": {
                  label: false,
                  title: "Post",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "役職",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
            },
          },
        },
        monitoring_agency_contacts: {
          "ui:options": {
            title: "Monitoring Agencies",
            itemTitle: "Monitoring Agency",
            addButtonTitle: "Add Monitoring Agency",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                addButtonTitle: "モニタリング担当機関を追加",
              },
            },
          },
          items: {
            monitoring_agency: {
              "ui:options": {
                label: false,
                title: "Responsible Organization for Monitoring Agency",
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "モニタリング担当機関",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-2",
                },
              },
            },
            person_in_charge_of_monitoring: {
              "ui:options": {
                title: "Responsible Person for Monitoring Agency",
                translations: {
                  jp: {
                    title: "モニタリング担当責任者",
                  },
                },
                classNames: objectClassNames.contact_root_container,
              },
              full_name: {
                "ui:options": {
                  label: false,
                  title: "Name",
                  translations: {
                    jp: {
                      title: "氏名",
                    },
                  },
                  classNames: objectClassNames.contact_nested_container,
                },
                surname: {
                  "ui:options": {
                    label: false,
                    title: "Last",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名字",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                given_name: {
                  "ui:options": {
                    label: false,
                    title: "First",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名前",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
              },
              erad_number: {
                "ui:options": {
                  label: false,
                  title: "e-Rad Number",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 10,
                  translations: {
                    jp: {
                      title: "e-Rad番号",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
              affiliation: {
                "ui:options": {
                  label: false,
                  title: "Affiliation",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "所属",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              post: {
                "ui:options": {
                  label: false,
                  title: "Post",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "役職",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-1-3",
                  },
                },
              },
            },
          },
        },
        auditing_agency_contacts: {
          "ui:options": {
            title: "Auditing Agencies",
            itemTitle: "Auditing Agency",
            addButtonTitle: "Add Auditing Agency",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                addButtonTitle: "監査担当機関を追加",
              },
            },
          },
          items: {
            auditing_agency: {
              "ui:options": {
                label: false,
                title: "Responsible Organization for Trial Audit",
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "監査担当機関",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-2",
                },
              },
            },
            auditing_manager: {
              "ui:options": {
                title: "Responsible Person for Trial Audit",
                translations: {
                  jp: {
                    title: "監査担当責任者",
                  },
                },
                classNames: objectClassNames.contact_root_container,
              },
              full_name: {
                "ui:options": {
                  label: false,
                  title: "Name",
                  translations: {
                    jp: {
                      title: "氏名",
                    },
                  },
                  classNames: objectClassNames.contact_nested_container,
                },
                surname: {
                  "ui:options": {
                    label: false,
                    title: "Last",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名字",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                given_name: {
                  "ui:options": {
                    label: false,
                    title: "First",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名前",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
              },
              erad_number: {
                "ui:options": {
                  label: false,
                  title: "e-Rad Number",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 10,
                  translations: {
                    jp: {
                      title: "e-Rad番号",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
              affiliation: {
                "ui:options": {
                  label: false,
                  title: "Affiliation",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "所属",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              post: {
                "ui:options": {
                  label: false,
                  title: "Post",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "役職",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-1-3",
                  },
                },
              },
            },
          },
        },
        statistical_analysis_contact: {
          "ui:options": {
            title: "Statistical Analysis Contact",
            classNames: objectClassNames.tertiary,
          },
          organization_in_charge_of_statistical_analysis: {
            "ui:options": {
              label: false,
              title: "Responsible Organization for Statistical Analysis",
              disableValidation,
              charCountEnabled: true,
              maxChars: 100,
              translations: {
                jp: {
                  title: "統計解析担当機関",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-1-2",
              },
            },
          },
          head_of_statistical_analysis: {
            "ui:options": {
              title: "Responsible Person for Statistical Analysis",
              translations: {
                jp: {
                  title: "統計解析担当責任者",
                },
              },
              classNames: objectClassNames.contact_root_container,
            },
            full_name: {
              "ui:options": {
                label: false,
                title: "Name",
                translations: {
                  jp: {
                    title: "氏名",
                  },
                },
                classNames: objectClassNames.contact_nested_container,
              },
              surname: {
                "ui:options": {
                  label: false,
                  title: "Last",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "名字",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              given_name: {
                "ui:options": {
                  label: false,
                  title: "First",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "名前",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
            erad_number: {
              "ui:options": {
                label: false,
                title: "e-Rad Number",
                disableValidation,
                charCountEnabled: true,
                maxChars: 10,
                translations: {
                  jp: {
                    title: "e-Rad番号",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-md",
                },
              },
            },
            affiliation: {
              "ui:options": {
                label: false,
                title: "Affiliation",
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "所属",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            post: {
              "ui:options": {
                label: false,
                title: "Post",
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "役職",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-3",
                },
              },
            },
          },
        },
        organization_in_charge_of_research_and_development_planning_support_contact: {
          "ui:options": {
            title: "Research and Development Planning Support",
            classNames: objectClassNames.tertiary,
          },
          organization_in_charge_of_research_and_development_planning_support: {
            "ui:options": {
              label: false,
              title: "Responsible Organization for Clinical Operations",
              disableValidation,
              charCountEnabled: true,
              maxChars: 100,
              translations: {
                jp: {
                  title: "研究・開発計画支援担当機関",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-1-2",
              },
            },
          },
          research_and_development_planning_support_person: {
            "ui:options": {
              title: "Responsible person for Clinical Operations ",
              translations: {
                jp: {
                  title: "研究・開発計画支援担当者",
                },
              },
              classNames: objectClassNames.contact_root_container,
            },
            full_name: {
              "ui:options": {
                label: false,
                title: "Name",
                translations: {
                  jp: {
                    title: "氏名",
                  },
                },
                classNames: objectClassNames.contact_nested_container,
              },
              surname: {
                "ui:options": {
                  label: false,
                  title: "Last",
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "名字",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              given_name: {
                "ui:options": {
                  label: false,
                  title: "First",
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "名前",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
            erad_number: {
              "ui:options": {
                label: false,
                title: "e-Rad Number",
                disableValidation,
                charCountEnabled: true,
                maxChars: 10,
                translations: {
                  jp: {
                    title: "e-Rad番号",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-md",
                },
              },
            },
            affiliation: {
              "ui:options": {
                label: false,
                title: "Affiliation",
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "所属",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            post: {
              "ui:options": {
                label: false,
                title: "Post",
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "役職",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-3",
                },
              },
            },
          },
        },
        coordination_and_management_agency_contact: {
          "ui:options": {
            title: "Coordination and Management",
            classNames: objectClassNames.tertiary,
          },
          coordination_and_management_agency: {
            "ui:options": {
              label: false,
              title: "Responsible Organization for Coordination and Management",
              disableValidation,
              charCountEnabled: true,
              maxChars: 100,
              translations: {
                jp: {
                  title: "調整・管理実務担当機関",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-1-2",
              },
            },
          },
          coordination_and_management_personnel: {
            "ui:options": {
              title: "Responsible Person for Coordination and Management",
              translations: {
                jp: {
                  title: "調整・管理実務担当者",
                },
              },
              classNames: objectClassNames.contact_root_container,
            },
            full_name: {
              "ui:options": {
                label: false,
                title: "Name",
                translations: {
                  jp: {
                    title: "氏名",
                  },
                },
                classNames: objectClassNames.contact_nested_container,
              },
              surname: {
                "ui:options": {
                  label: false,
                  title: "Last",
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "名字",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              given_name: {
                "ui:options": {
                  label: false,
                  title: "First",
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "名前",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
            erad_number: {
              "ui:options": {
                label: false,
                title: "e-Rad Number",
                disableValidation,
                charCountEnabled: true,
                maxChars: 10,
                translations: {
                  jp: {
                    title: "e-Rad番号",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-md",
                },
              },
            },
            affiliation: {
              "ui:options": {
                label: false,
                title: "Affiliation",
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "所属",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            post: {
              "ui:options": {
                label: false,
                title: "Post",
                disableValidation,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "役職",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-3",
                },
              },
            },
          },
        },
        person_in_charge_of_other_research_contacts: {
          "ui:options": {
            title: "Other Research Personnel",
            itemTitle: "Other Research Personnel",
            addButtonTitle: "Add Other Research Personnel",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                title: "",
                addButtonTitle: "その他の研究を総括する者を追加",
              },
            },
          },
          items: {
            person_in_charge_of_other_research: {
              "ui:options": {
                title: "Responsible Person of Other Research",
                translations: {
                  jp: {
                    title: "その他の研究を総括する者",
                  },
                },
                classNames: objectClassNames.contact_root_container,
              },
              full_name: {
                "ui:options": {
                  label: false,
                  title: "Name",
                  translations: {
                    jp: {
                      title: "氏名",
                    },
                  },
                  classNames: objectClassNames.contact_nested_container,
                },
                surname: {
                  "ui:options": {
                    label: false,
                    title: "Last",
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名字",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                given_name: {
                  "ui:options": {
                    label: false,
                    title: "First",
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名前",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
              },
              erad_number: {
                "ui:options": {
                  label: false,
                  title: "e-Rad Number",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 10,
                  translations: {
                    jp: {
                      title: "e-Rad番号",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
              affiliation: {
                "ui:options": {
                  label: false,
                  title: "Affiliation",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "所属",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              post: {
                "ui:options": {
                  label: false,
                  title: "Post",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "役職",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-1-3",
                  },
                },
              },
              applicability_of_secondary_sponsor: {
                "ui:options": {
                  label: false,
                  title: "Applicability of Secondary Sponsor",
                  tooltip:
                    "A Secondary Sponsor is a person who, in collaboration with the Primary Sponsor, is responsible for planning the research and procuring research funds.",
                  translations: {
                    jp: {
                      title: "Secondary Sponsorの該当性",
                      tooltip:
                        "二次スポンサーとは、一次スポンサーと共同で、研究の企画立案や研究費の調達を担当する者をいいます",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-full",
                  },
                },
                "ui:widget": getWidget({ type: "radio", preview }),
              },
            },
          },
        },
      },
      information_on_multicenter_clinical_trials: {
        "ui:options": {
          title: "Multicenter Clinical Trials Information",
          translations: {
            jp: {
              title: "多施設共同治験の情報",
            },
          },
          classNames: objectClassNames.primary,
        },
        "ui:order": ["*"],
        applicability_of_multicenter: {
          "ui:options": {
            label: false,
            tooltip:
              "If 'Yes', please enter joint research institution information in the fields below. If you have a large number of registration plans, please upload the document in the documents tab.  Please use  the multicenter joint research institution information template file.",
            tooltipUrl: "https://jrct.niph.go.jp/re/reports/template/joint_template",
            tooltipUrlTitle: "Download template",
            title: "Applicability of multicenter joint study",
            translations: {
              jp: {
                title: "多施設共同試験等の該当の有無",
                tooltip:
                  "「あり」を選択された場合は、以下の項目に共同研究機関情報を入力してください。登録予定件数が多い場合はExcelファイルをインポートすることも可能です",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-2",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        joint_research_institution_information: {
          "ui:options": {
            title: "Joint Research Institutions",
            itemTitle: "Joint Research Institution",
            addButtonTitle: "Add Joint Research Institution ",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                addButtonTitle: "共同研究機関情報追加",
              },
            },
          },
          items: {
            investigator_contact_informations: {
              "ui:options": {
                title: "Primary Investigator Information",
                translations: {
                  jp: {
                    title: "治験責任医師等の連絡先",
                  },
                },
                classNames: objectClassNames.tertiary,
              },
              full_name: {
                "ui:options": {
                  label: false,
                  title: "Name",
                  translations: {
                    jp: {
                      title: "氏名",
                    },
                  },
                  classNames: {
                    objectTitle: "rjs-h3",
                    fieldContainer: "rjs-full",
                    field: "rjs-col-1-2",
                  },
                },
                surname: {
                  "ui:options": {
                    label: false,
                    title: "Last",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "姓",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                given_name: {
                  "ui:options": {
                    label: false,
                    title: "First",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
              },
              erad_number: {
                "ui:options": {
                  label: false,
                  title: "e-Rad Number",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 10,
                  translations: {
                    jp: {
                      title: "e-Rad番号",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
              affiliation: {
                "ui:options": {
                  label: false,
                  title: "Affiliation",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 255,
                  translations: {
                    jp: {
                      title: "所属機関",
                      maxChars: 200,
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              department: {
                "ui:options": {
                  label: false,
                  title: "Affiliated Department",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "所属部署",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              postal_code: {
                "ui:options": {
                  label: false,
                  title: "Postal code",
                  tooltip: "Please enter numbers and half-width hyphens",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 8,
                  translations: {
                    jp: {
                      title: "所属機関の郵便番号",
                      tooltip: "数値・半角ハイフンで入力してください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
              prefecture: {
                "ui:options": {
                  label: false,
                  title: "Prefecture",
                  translations: {
                    jp: {
                      title: "所属機関の所在都道府県",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              address: {
                "ui:options": {
                  label: false,
                  title: "Address",
                  tooltip: "Please enter with prefecture",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "所属機関の住所",
                      tooltip: "都道府県でご入力ください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-xl",
                  },
                },
              },
              phone: {
                "ui:options": {
                  label: false,
                  title: "Phone",
                  tooltip: "Please enter numbers and half-width hyphens",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "電話番号",
                      tooltip: "数値・半角ハイフンで入力してください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                    field: "rjs-col-md",
                  },
                },
              },
              email: {
                "ui:options": {
                  label: false,
                  title: "Email",
                  tooltip: "Please enter in email address format",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "電子メールアドレス",
                      tooltip: "メールアドレス形式で入力してください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
            },
            research_contact_information: {
              "ui:options": {
                title: "Research Contact Information",
                translations: {
                  jp: {
                    title: "研究に関する問い合わせ先",
                  },
                },
                classNames: objectClassNames.contact_root_container,
              },
              full_name: {
                "ui:options": {
                  label: false,
                  title: "Name",
                  translations: {
                    jp: {
                      title: "氏名",
                    },
                  },
                  classNames: {
                    objectTitle: "rjs-h3",
                    fieldContainer: "rjs-full",
                    field: "rjs-col-1-2",
                  },
                },
                surname: {
                  "ui:options": {
                    label: false,
                    title: "Last",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "姓",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
                given_name: {
                  "ui:options": {
                    label: false,
                    title: "First",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 40,
                    translations: {
                      jp: {
                        title: "名",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-lg",
                    },
                  },
                },
              },
              institution: {
                "ui:options": {
                  label: false,
                  title: "Institution",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "担当者の所属機関",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-1-3",
                  },
                },
              },
              department: {
                "ui:options": {
                  label: false,
                  title: "Affiliated Department",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "担当者所属部署",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              postal_code: {
                "ui:options": {
                  label: false,
                  title: "Postal code",
                  tooltip: "Please enter numbers and half-width hyphens",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 8,
                  translations: {
                    jp: {
                      title: "担当者所属機関の郵便番号",
                      tooltip: "数値・半角ハイフンで入力してください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
              prefecture: {
                "ui:options": {
                  label: false,
                  title: "Prefecture",
                  translations: {
                    jp: {
                      title: "担当者所属機関の所在都道府県",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              address: {
                "ui:options": {
                  label: false,
                  title: "Address",
                  tooltip: "Please enter with prefecture",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "所属機関の住所",
                      tooltip: "都道府県でご入力ください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-xl",
                  },
                },
              },
              phone: {
                "ui:options": {
                  label: false,
                  title: "Phone",
                  tooltip: "Please enter numbers and half-width hyphens",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "電話番号",
                      tooltip: "数値・半角ハイフンで入力してください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                    field: "rjs-col-md",
                  },
                },
              },
              fax: {
                "ui:options": {
                  label: false,
                  title: "Fax",
                  tooltip: "Please enter numbers and half-width hyphen",
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "Fax番号",
                      tooltip: "数値・半角ハイフンで入力してください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                    field: "rjs-col-md",
                  },
                },
              },
              email: {
                "ui:options": {
                  label: false,
                  title: "Email",
                  tooltip: "Please enter in email address format",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "電子メールアドレス",
                      tooltip: "メールアドレス形式で入力してください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-md",
                  },
                },
              },
            },
            medical_institution_name: {
              "ui:options": {
                label: false,
                title: "Person Responsible for Medical Institution",
                translations: {
                  jp: {
                    title: "実施医療機関の長の氏名",
                  },
                },
                classNames: objectClassNames.contact_root_container,
              },
              surname: {
                "ui:options": {
                  label: false,
                  title: "Last",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "姓",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              given_name: {
                "ui:options": {
                  label: false,
                  title: "First",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 40,
                  translations: {
                    jp: {
                      title: "名",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
            },
            administrator_permission: {
              "ui:options": {
                label: false,
                title: "Administrator’s permission for study",
                translations: {
                  jp: {
                    title: "管理者の許可の有無",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-3",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            approval_date: {
              "ui:options": {
                label: false,
                title: "Approval date of first IRB",
                tooltip:
                  "Please enter the date on which the IRB approved the clinical trial protocol that describes this medical institution.",
                translations: {
                  jp: {
                    title: "IRBの承認日",
                    tooltip: "本実施医療機関について記載された治験実施計画がIRBに承認された日を入力してください",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-3",
                  field: "rjs-col-date",
                },
              },
              "ui:widget": getWidget({ type: "date", preview }),
            },
            facilities_equipment_necessary_for_emergency_medical_care: {
              "ui:options": {
                label: false,
                title: "Facilities for emergency medical care",
                disableValidation,
                charCountEnabled: true,
                maxChars: 250,
                translations: {
                  jp: {
                    title: "救急医療に必要な施設又は設備",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-2",
                },
              },
            },
          },
        },
      },
      overview_of_pharmaceuticals: {
        "ui:options": {
          title: "Summary of Investigational Products",
          translations: {
            jp: {
              title: "医薬品等の概要",
            },
          },
          classNames: objectClassNames.primary,
        },
        "ui:order": ["*"],
        investigational_products: {
          "ui:options": {
            title: "Overview of investigational products used for testing",
            addButtonTitle: "Add Investigational Product",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                title: "試験等に用いる医薬品等の概要",
                addButtonTitle: "医薬品等の追加",
              },
            },
          },
          items: {
            "ui:options": {
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:order": ["intervention_type", "approval_status", "general_name", "study_drug_provider"],
            intervention_type: {
              "ui:options": {
                label: false,
                title: "Type of Intervention",
                translations: {
                  jp: {
                    title: "医薬品、医療機器、再生医療等製品の別",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            approval_status: {
              "ui:options": {
                label: false,
                title: "Approval Status under the Pharmaceutical and Medical Device Act",
                translations: {
                  jp: {
                    title: "医薬品医療機器等法における未承認、適応外、承認内の別",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            general_name: {
              "ui:options": {
                title: "Generic Name",
                translations: {
                  jp: {
                    title: "一般名称等",
                  },
                },
                classNames: objectClassNames.tertiary,
              },
              pharmaceuticals: {
                "ui:options": {
                  title: "Pharmaceuticals",
                  translations: {
                    jp: {
                      title: "医薬品",
                    },
                  },
                  classNames: objectClassNames.tertiary,
                },
                generic_name: {
                  "ui:options": {
                    label: false,
                    title: "Generic name",
                    tooltip:
                      "If the product is not approved domestically or overseas, please enter the development code",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 100,
                    translations: {
                      jp: {
                        title: "一般名称",
                        tooltip: "国内外で未承認の場合は開発コードを入力してください",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
                brand_name: {
                  "ui:options": {
                    label: false,
                    title: "Brand name",
                    tooltip: "For overseas products, please also enter the country name",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 100,
                    translations: {
                      jp: {
                        title: "販売名",
                        tooltip: "海外製品の場合は国名も入力してください",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
                approval_number: {
                  "ui:options": {
                    label: false,
                    title: "Approval number",
                    tooltip: "If not approved, please enter 'None'",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 50,
                    translations: {
                      jp: {
                        title: "承認番号",
                        tooltip: "未承認の場合は「なし」と入力してください",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
              },
              medical_equipment: {
                "ui:options": {
                  title: "Medical Equipment",
                  translations: {
                    jp: {
                      title: "医療機器",
                    },
                  },
                  classNames: objectClassNames.tertiary,
                },
                classification: {
                  "ui:options": {
                    label: false,
                    title: "Classification",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 100,
                    translations: {
                      jp: {
                        title: "類別",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
                generic_name: {
                  "ui:options": {
                    label: false,
                    title: "Generic name",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 100,
                    translations: {
                      jp: {
                        title: "一般的名称",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
                approval_number: {
                  "ui:options": {
                    label: false,
                    title: "Approval/Certification/Notification number",
                    tooltip: "If not approved, please enter 'None'",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 50,
                    translations: {
                      jp: {
                        title: "承認・認証・届出番号",
                        tooltip: "未承認の場合は「なし」と入力してください",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
              },
              regenerative_medicine_products: {
                "ui:options": {
                  title: "Regenerative Medicine Products",
                  translations: {
                    jp: {
                      title: "再生医療等製品",
                    },
                  },
                  classNames: objectClassNames.tertiary,
                },
                classification: {
                  "ui:options": {
                    label: false,
                    title: "Classification",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 100,
                    translations: {
                      jp: {
                        title: "類別",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
                generic_name: {
                  "ui:options": {
                    label: false,
                    title: "Generic name",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 100,
                    translations: {
                      jp: {
                        title: "一般的名称",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
                approval_number: {
                  "ui:options": {
                    label: false,
                    title: "Approval number",
                    tooltip: "If not approved, please enter 'None'",
                    disableValidation,
                    charCountEnabled: true,
                    maxChars: 50,
                    translations: {
                      jp: {
                        title: "承認番号",
                        tooltip: "未承認の場合は「なし」と入力してください",
                      },
                    },
                    classNames: {
                      fieldContainer: "rjs-col-1-2",
                    },
                  },
                },
              },
            },
            study_drug_provider: {
              "ui:options": {
                title: "Study Drug Provider",
                translations: {
                  jp: {
                    title: "被験薬等提供者",
                  },
                },
                classNames: objectClassNames.tertiary,
              },
              name: {
                "ui:options": {
                  label: false,
                  title: "Name",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "名称",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-xl",
                  },
                },
              },
              prefecture: {
                "ui:options": {
                  label: false,
                  title: "Prefecture",
                  translations: {
                    jp: {
                      title: "所在都道府県",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-lg",
                  },
                },
              },
              location: {
                "ui:options": {
                  label: false,
                  title: "Location",
                  tooltip: "Please enter with prefecture",
                  disableValidation,
                  charCountEnabled: true,
                  maxChars: 100,
                  translations: {
                    jp: {
                      title: "所在地",
                      tooltip: "都道府県でご入力ください",
                    },
                  },
                  classNames: {
                    fieldContainer: "rjs-col-xl",
                  },
                },
              },
            },
          },
        },
      },
      study_timeline: {
        "ui:options": {
          title: "Study Status and Compensation Information",
          translations: {
            jp: {
              title: "実施状況の確認及び補償",
            },
          },
          classNames: objectClassNames.primary,
        },
        "ui:order": ["plan_sponsor_audit", "*"],
        plan_sponsor_audit: {
          "ui:options": {
            label: false,
            title: "Plan to  conduct a sponsor audit?",
            translations: {
              jp: {
                title: "監査の実施予定の有無?",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-3",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        recruitment: {
          "ui:options": {
            label: false,
            title: "Overall Study Status",
            translations: {
              jp: {
                title: "試験等の進捗状況",
              },
            },
            classNames: objectClassNames.tertiary,
          },
          recruitment_status: {
            "ui:options": {
              label: false,
              title: "Recruitment Status",
              tooltip: "If you want to start research immediately after publication, please select 'Recruiting'",
              required: true,
              translations: {
                jp: {
                  title: "進捗状況",
                  tooltip: "公開後すぐに研究を開始する場合には「募集中 」を選択してください",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
          },
          summary_results: {
            "ui:options": {
              label: false,
              title: "Summary Results (Primary Outcome Results)",
              tooltip: "When primary results are available, please enter them by submitting a change notification",
              disableValidation,
              charCountEnabled: true,
              maxChars: 512,
              translations: {
                jp: {
                  title: "主たる評価項目に係る研究成果",
                  tooltip: "主たる評価項目に係る研究成果が明らかになった際、変更届により入力を行ってください。",
                  maxChars: 400,
                },
              },
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "textarea", preview }),
          },
          ...setHiddenFields(hideFields.study_timeline.recruitment),
        },
        compensation_and_medical_care_in_the_event_of_health_damage: {
          "ui:options": {
            label: false,
            title: "Information regarding compensation and medical care in the event of health damage to test subjects",
            translations: {
              jp: {
                title: "試験等の対象者に健康被害が生じた場合の補償及び医療の提供に関する事項",
              },
            },
            classNames: objectClassNames.tertiary,
          },
          compensation_for_subjects: {
            "ui:options": {
              label: false,
              title: "Compensation for subjects",
              translations: {
                jp: {
                  title: "研究対象者への補償の有無",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-1-3",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          contents_of_compensation: {
            "ui:options": {
              label: false,
              title: "Contents of compensation",
              translations: {
                jp: {
                  title: "補償の内容",
                },
              },
              classNames: objectClassNames.tertiary,
            },
            with_insurance: {
              "ui:options": {
                label: false,
                title: "With or without insurance?",
                translations: {
                  jp: {
                    title: "保険への加入の有無",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            insurance_coverage: {
              "ui:options": {
                label: false,
                title: "Insurance coverage",
                disableValidation,
                charCountEnabled: true,
                maxChars: 400,
                translations: {
                  jp: {
                    title: "保険の補償内容",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            non_insurance_coverage: {
              "ui:options": {
                label: false,
                title: "Non-insurance coverage",
                tooltip: "If you do not have insurance  please enter 'None'",
                disableValidation,
                charCountEnabled: true,
                maxChars: 400,
                translations: {
                  jp: {
                    title: "保険以外の補償の内容",
                    tooltip: "保険加入している場合であって、なしの場合は「なし」と入力してください",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        ...setHiddenFields(hideFields.study_timeline.root),
      },
      monetary_sponsor: {
        "ui:options": {
          title: "Monetary Sponsor",
          translations: {
            jp: {
              title: "研究資金等の提供等",
            },
          },
          classNames: objectClassNames.primary,
        },
        "ui:order": ["*"],
        primary_sponsors: {
          "ui:options": {
            title: "Sponsor Information",
            itemTitle: "Primary Sponsor Information",
            addButtonTitle: "Add Primary Sponsor",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                title: "依頼者等に関する事項",
                itemTitle: "依頼者等に関する事項",
                addButtonTitle: "依頼者等からの研究資金等の提供の追加",
              },
            },
          },
          items: {
            "ui:order": ["primary_sponsor_name", "monetary_support", "source_of_monetary_support", "*"],
            primary_sponsor_name: {
              "ui:options": {
                label: false,
                title: "Primary Sponsor Name",
                tooltip: "For Investigator Sponsored clinical trials, please leave blank",
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "依頼者等の名称",
                    tooltip: "試験等の依頼者の名称を入力してください。医師主導治験の場合は空欄にしてください。",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                  field: "rjs-col-lg",
                },
              },
            },
            monetary_support: {
              "ui:options": {
                label: false,
                title: "Monetary Support?",
                translations: {
                  jp: {
                    title: "研究資金提供の有無",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            source_of_monetary_support: {
              "ui:options": {
                label: false,
                title: "Source of Monetary Support",
                disableValidation,
                charCountEnabled: true,
                maxChars: 2000,
                translations: {
                  jp: {
                    title: "資金援助の財源",
                  },
                },
                classNames: {
                  fieldContainer: "fill-width-75",
                },
              },
            },
            contract_has_been_concluded: {
              "ui:options": {
                label: false,
                title: "Contract has been concluded",
                translations: {
                  jp: {
                    title: "契約締結の有無",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            contract_date: {
              "ui:options": {
                label: false,
                title: "Contract date",
                translations: {
                  jp: {
                    title: "契約締結日",
                  },
                },
                classNames: {
                  fieldContainer: "fill-width-75",
                  field: "rjs-col-date",
                },
              },
              "ui:widget": getWidget({ type: "date", preview }),
            },
            any_goods_support: {
              "ui:options": {
                label: false,
                title: "Any Goods Support?",
                translations: {
                  jp: {
                    title: "物品提供の有無",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            contents_of_goods_supported: {
              "ui:options": {
                label: false,
                title: "Contents of goods supported",
                disableValidation,
                charCountEnabled: true,
                maxChars: 200,
                translations: {
                  jp: {
                    title: "物品提供の内容",
                  },
                },
                classNames: {
                  fieldContainer: "fill-width-75",
                },
              },
            },
            any_resources_support: {
              "ui:options": {
                label: false,
                title: "Any resources support?",
                translations: {
                  jp: {
                    title: "役務提供の有無",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            contents_of_resources_supported: {
              "ui:options": {
                label: false,
                title: "Contents of resources supported",
                disableValidation,
                charCountEnabled: true,
                maxChars: 200,
                translations: {
                  jp: {
                    title: "役務提供の内容",
                  },
                },
                classNames: {
                  fieldContainer: "fill-width-75",
                },
              },
            },
          },
        },
        other_sponsors: {
          "ui:options": {
            title: "Other Sponsors",
            itemTitle: "Other Sponsor",
            addButtonTitle: "Add Other Sponsor",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                addButtonTitle: "依頼者以外の企業からの研究資金等の提供組織を追加",
              },
            },
          },
          items: {
            with_other_sponsor: {
              "ui:options": {
                label: false,
                title: "Presence of Other Sponsor",
                required: true,
                translations: {
                  jp: {
                    title: "研究資金等の提供組織の有無",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
            source_of_monetary_support: {
              "ui:options": {
                label: false,
                title: "Source of Monetary Support",
                disableValidation,
                charCountEnabled: true,
                maxChars: 1000,
                translations: {
                  jp: {
                    title: "研究資金等の提供組織名称",
                    maxChars: 800,
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
            applicability_of_secondary_sponsor: {
              "ui:options": {
                label: false,
                title: "Applicability of Secondary Sponsor",
                tooltip:
                  "A Secondary Sponsor is a person who, in collaboration with the Primary Sponsor, is responsible for planning the research and procuring research funds",
                translations: {
                  jp: {
                    title: "Secondary Sponsorの該当性",
                    tooltip:
                      "二次支援者とは、一次支援研究者と共同で、研究の企画立案や研究費の調達を担当する者をいいます",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-2xl",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
          },
        },
      },
      irb_information: {
        "ui:options": {
          title: "IRB Information",
          translations: {
            jp: {
              title: "委員会の情報",
            },
          },
          classNames: objectClassNames.primary,
        },
        "ui:order": ["*"],
        irbs: {
          "ui:options": {
            itemTitle: "Name of IRB",
            addButtonTitle: "Add IRB Information",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                itemTitle: "IRBの名称等",
                addButtonTitle: "委員会の情報追加",
              },
            },
          },
          items: {
            irb_name: {
              "ui:options": {
                label: false,
                title: "Name of IRB",
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 255,
                translations: {
                  jp: {
                    title: "IRBの名称",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-3",
                },
              },
            },
            prefecture: {
              "ui:options": {
                label: false,
                title: "Prefecture",
                translations: {
                  jp: {
                    title: "都道府県",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-lg",
                },
              },
            },
            address: {
              "ui:options": {
                label: false,
                tooltip: "Please enter with prefecture",
                title: "Address",
                required: true,
                disableValidation,
                charCountEnabled: true,
                maxChars: 255,
                translations: {
                  jp: {
                    title: "住所",
                    tooltip: "市区町村以降を入力してください。例）さいたま市、千代田区",
                    maxChars: 200,
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-xl",
                },
              },
            },
            phone: {
              "ui:options": {
                label: false,
                title: "Phone",
                tooltip: "Please enter numbers and half-width hyphens",
                disableValidation,
                charCountEnabled: true,
                maxChars: 40,
                translations: {
                  jp: {
                    title: "電話番号",
                    tooltip: "数値・半角ハイフンで入力してください",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-md",
                  field: "rjs-col-md",
                },
              },
            },
            email: {
              "ui:options": {
                label: false,
                title: "Email",
                tooltip: "Please enter in email address format",
                disableValidation,
                charCountEnabled: true,
                maxChars: 255,
                translations: {
                  jp: {
                    title: "電子メールアドレス",
                    tooltip: "メールアドレス形式で入力してください",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-md",
                },
              },
            },
            irb_application_number: {
              "ui:options": {
                label: false,
                title: "IRB Application Number",
                tooltip: "If you have a receipt number issued by the IRB, please enter it.",
                disableValidation,
                charCountEnabled: true,
                maxChars: 30,
                translations: {
                  jp: {
                    title: "審査受付番号",
                    tooltip: "IRBから発行された受付番号がある場合は、入力してください",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-1-3",
                },
              },
            },
            irb_results: {
              "ui:options": {
                label: false,
                title: "Results of IRB",
                translations: {
                  jp: {
                    title: "当該試験等に対する審査結果",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "radio", preview }),
            },
          },
        },
      },
      study_ipd: {
        "ui:options": {
          title: "Other Information",
          translations: {
            jp: {
              title: "その他の事項",
            },
          },
          classNames: objectClassNames.primary,
        },
        "ui:order": [
          "contents_of_informed_consent_for_study",
          "other_registries",
          "notes",
          "plan_to_share_IPD",
          "plan_description",
          "supplementary_information",
          "*",
        ],
        contents_of_informed_consent_for_study: {
          "ui:options": {
            label: false,
            title: "Contents of Informed Consent for Study",
            tooltip:
              "If it is not possible to describe all of the items to be described, write 'As shown in the attachment' in the same column and attach an attachment. This content will not be made public",
            disableValidation,
            charCountEnabled: true,
            maxChars: 400,
            translations: {
              jp: {
                title: "臨床研究・治験の対象者等への説明及び同意の内容",
                tooltip:
                  "その記載事項の全てを記載する事ができないときには、同欄に「別紙のとおり。」と記載し、別紙を添付すること。なお、この内容は公表されません。",
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        other_registries: {
          "ui:options": {
            title: "Other Registry Study ID",
            addButtonTitle: "Add Other Registry Study ID",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: arrayClassNames.primary,
            translations: {
              jp: {
                title: "他の臨床研究登録機関への登録",
                addButtonTitle: "他の臨床研究登録機関発行の研究番号の追加",
              },
            },
          },
          items: {
            other_registry_study_id: {
              "ui:options": {
                label: false,
                title: "Other Registry Study ID",
                disableValidation,
                charCountEnabled: true,
                hideFork: true,
                maxChars: 50,
                translations: {
                  jp: {
                    title: "他の臨床研究登録機関発行の研究番号",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-2xl",
                },
              },
            },
            issuing_authority: {
              "ui:options": {
                label: false,
                title: "Issuing Authority",
                disableValidation,
                hideFork: true,
                charCountEnabled: true,
                maxChars: 100,
                translations: {
                  jp: {
                    title: "他の臨床研究登録機関の名称",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-col-2xl",
                },
              },
            },
          },
        },
        notes: {
          "ui:options": {
            title: "Notes",
            translations: {
              jp: {
                title: "臨床研究を実施するに当たって留意すべき事項",
              },
            },
            classNames: objectClassNames.tertiary,
          },
          multicenter_study: {
            "ui:options": {
              label: false,
              title: "Multiregional Study",
              translations: {
                jp: {
                  title: "国際共同研究",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-1-3",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          countries: {
            "ui:options": {
              label: false,
              title: "Countries of recruitment (except Japan)",
              translations: {
                jp: {
                  title: "臨床研究を実施する国（日本以外）",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-lg",
              },
            },
            "ui:widget": getWidget({ type: "SelectTag", preview }),
          },
          presence_combined_treatment_not_covered_by_insurance: {
            "ui:options": {
              label: false,
              title: "Presence of combined treatment not covered by insurance",
              translations: {
                jp: {
                  title: "保険外併用療養の有無",
                },
              },
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          research_under_guidelines_of_gene_therapy: {
            "ui:options": {
              label: false,
              title:
                "Research under the Ministry of Health, Labor and Welfare's (MHLW's) Guidelines Clinical Research of Gene Therapy?",
              translations: {
                jp: {
                  title: "遺伝子治療等臨床研究に関する指針（平成27年厚生労働省令第344号）の対象となる試験等",
                },
              },
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          research_under_law_of_ensuring_biodiversity: {
            "ui:options": {
              label: false,
              title: "Research under the MHLW's law of ensuring Biodiversity?",
              translations: {
                jp: {
                  title:
                    "遺伝子組換え生物等の使用等の規制による生物の多様性の確保に関する法律 （平成15年法律第97号） の対象となる薬物を用いる試験等",
                },
              },
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
          research_with_drugs_designed_as_biological_products: {
            "ui:options": {
              label: false,
              title: "Research with Drugs Expected to be designated as Biological Products?",
              translations: {
                jp: {
                  title: "生物由来製品に指定が見込まれる薬物を用いる試験等",
                },
              },
              classNames: {
                fieldContainer: "rjs-full",
              },
            },
            "ui:widget": getWidget({ type: "radio", preview }),
          },
        },
        plan_to_share_IPD: {
          "ui:options": {
            title: "Plan to Share IPD",
            label: false,
            translations: {
              jp: {
                title: "IPDデータを共有する計画",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-3",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        plan_description: {
          "ui:options": {
            title: "Plan Description",
            errorMessage: "IPD Description must have no more than 1000 characters",
            label: false,
            disableValidation,
            charCountEnabled: true,
            maxChars: 2000,
            translations: {
              jp: {
                title: "計画の説明",
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        supplementary_information: {
          "ui:options": {
            title: "Supplementary information",
            itemTitle: "Other",
            addButtonTitle: "Add Supplementary Information",
            label: false,
            addable: true,
            removable: true,
            orderable: true,
            classNames: {
              ...arrayClassNames.singleField,
              actionContainer: "rjs-full",
            },
            translations: {
              jp: {
                title: "全体を通しての補足事項等",
                addButtonTitle: "補足情報の追加",
              },
            },
          },
          items: {
            others: {
              "ui:options": {
                title: "Others",
                label: false,
                disableValidation,
                charCountEnabled: true,
                maxChars: 400,
                translations: {
                  jp: {
                    title: "その他",
                  },
                },
                classNames: {
                  fieldContainer: "rjs-full",
                },
              },
              "ui:widget": getWidget({ type: "textarea", preview }),
            },
          },
        },
        ...setHiddenFields(hideFields.study_ipd.root),
      },
      milestone_dates: {
        "ui:options": {
          title: "Study Objective and Summary",
          classNames: {
            fieldContainer: "rjs-full",
            objectContainer: "rjs-gap-16 pt-24 pb-24",
            objectTitleContainer: "rjs-h1-container",
            objectTitle: "rjs-h1",
          },
          translations: {
            jp: {
              title: "目的及び内容",
            },
          },
        },
        lpo: {
          "ui:options": {
            title: "Study Completion Date",
            classNames: objectClassNames.pactdate,
            translations: {
              jp: {
                title: "実施期間（終了日）",
              },
            },
          },
          baseline: {
            "ui:options": {
              title: "Baseline",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-date",
              },
              translations: {
                jp: {
                  title: "ベースライン",
                },
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          forecast: {
            "ui:options": {
              title: "Forecast",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-date",
              },
              translations: {
                jp: {
                  title: "予定",
                },
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          actual: {
            "ui:options": {
              title: "Actual",
              label: false,
              classNames: {
                fieldContainer: "rjs-col-date",
              },
              translations: {
                jp: {
                  title: "実際の終了日",
                },
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
        },
        ...setHiddenFields(hideFields.milestone_dates.root),
      },
      study_description: {
        "ui:options": {
          classNames: {
            fieldContainer: "rjs-full",
            objectContainer: "rjs-gap-16 pb-16",
          },
        },
        "ui:order": [
          "brief_summary",
          "start_date_of_enrollment",
          "study_start_date_in_japan",
          "study_conditions",
          "inclusion_criteria",
          "exclusion_criteria",
          "discontinuation_criteria",
          "medical_conditions",
          "target_disease_code",
          "study_keywords",
          "presence_of_interventions",
          "interventions",
          "intervention_code",
          "intervention_keywords",
          "primary_outcome_measures",
          "secondary_outcome_measures",
          "*",
        ],
        brief_summary: {
          "ui:options": {
            title: "Study Objectives",
            charCountEnabled: true,
            label: false,
            required: true,
            disableValidation,
            maxChars: 400,
            translations: {
              jp: {
                title: "試験等の目的",
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        start_date_of_enrollment: {
          "ui:order": ["planned_enrollment_start_date_type", "planned_enrollment_start_date", "*"],
          "ui:options": {
            label: false,
            title: "Start date of enrollment",
            translations: {
              jp: {
                title: "症例登録開始予定日",
              },
            },
            classNames: objectClassNames.tertiary,
          },
          planned_enrollment_start_date_type: {
            "ui:options": {
              label: false,
              title: "Planned Start Date of Enrollment Type",
              disableValidation,
              translations: {
                jp: {
                  title: "症例登録開始予定日",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-xl",
                field: "rjs-col-xl",
              },
            },
          },
          planned_enrollment_start_date: {
            "ui:options": {
              label: false,
              title: "Planned Start Date of Enrollment",
              disableValidation,
              translations: {
                jp: {
                  title: "日付",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-2xl",
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
          actual_enrollment_start_date: {
            "ui:options": {
              label: false,
              title: "Actual Start Date of Enrollment",
              disableValidation,
              translations: {
                jp: {
                  title: "第1症例登録日",
                },
              },
              classNames: {
                fieldContainer: "rjs-col-2xl",
                field: "rjs-col-date",
              },
            },
            "ui:widget": getWidget({ type: "date", preview }),
          },
        },
        study_start_date_in_japan: {
          "ui:options": {
            label: false,
            title: "Study start date in Japan",
            required: true,
            disableValidation,
            translations: {
              jp: {
                title: "実施期間（開始日）",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-2",
              field: "rjs-col-date",
            },
          },
          "ui:widget": getWidget({ type: "date", preview }),
        },
        inclusion_criteria: {
          "ui:options": {
            label: false,
            title: "Inclusion Criteria",
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 8000,
            customFnProps: [
              {
                tooltip: "Copy Inclusion Criteria's from CTIS Protocol",
                fnKey: "jrct-in-ex-criteria",
                fnArgs: {
                  type: "inclusion",
                },
              },
            ],
            translations: {
              jp: {
                title: "主たる選択基準",
                maxChars: 6000,
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        exclusion_criteria: {
          "ui:options": {
            label: false,
            title: "Exclusion Criteria",
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 8000,
            customFnProps: [
              {
                tooltip: "Copy Exclusion Criteria's from CTIS Protocol",
                fnKey: "jrct-in-ex-criteria",
                fnArgs: {
                  type: "exclusion",
                },
              },
            ],
            translations: {
              jp: {
                title: "主たる除外基準",
                maxChars: 6000,
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        discontinuation_criteria: {
          "ui:options": {
            label: false,
            title: "Discontinuation criteria",
            tooltip:
              "Please describe the subjects of each clinical study and the rules or criteria for discontinuing part or all of the clinical study",
            disableValidation,
            maxChars: 6000,
            charCountEnabled: true,
            translations: {
              jp: {
                title: "中止基準",
                tooltip:
                  "個々の臨床研究の対象者並びに臨床研究の一部及び全体の中止規定又は中止基準の説明を記載してください",
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        target_disease_code: {
          "ui:options": {
            label: false,
            title: "Target disease code",
            tooltip: "Please enter the corresponding MeSH code, use the link below for refenrence",
            tooltipUrl: "https://www.ncbi.nlm.nih.gov/mesh?cmd=Retrieve&dopt=Full&list_uids=6804665",
            tooltipUrlTitle: "MeSH Codes URL",
            maxChars: 200,
            charCountEnabled: true,
            translations: {
              jp: {
                title: "対象疾患コード",
                tooltip: "こちらを参考に該当のMeSHコードを入力してください。",
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        study_keywords: {
          "ui:options": {
            title: "Target Disease Keyword",
            label: false,
            disableValidation,
            translations: {
              jp: {
                title: "対象疾患キーワード",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-2",
            },
          },
          "ui:widget": getWidget({ type: "SelectTag", preview }),
        },
        presence_of_interventions: {
          "ui:options": {
            label: false,
            title: "Presence of intervention",
            required: true,
            disableValidation,
            translations: {
              jp: {
                title: "介入の有無",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-2",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        interventions: {
          "ui:options": {
            label: false,
            title: "Intervention(s)",
            disableValidation,
            charCountEnabled: true,
            maxChars: 8000,
            customFnProps: [
              {
                tooltip: "Combine all Arm title(s), type(s), and description(s) from CTG.",
                fnKey: "jrct-interventions",
              },
            ],
            translations: {
              jp: {
                title: "介入の内容",
                maxChars: 6000,
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        intervention_code: {
          "ui:options": {
            label: false,
            title: "Intervention Code",
            tooltip: "Please enter the corresponding MeSH code, use the link below for refenrence",
            tooltipUrl: "https://www.ncbi.nlm.nih.gov/mesh?cmd=Retrieve&dopt=Full&list_uids=6804665",
            tooltipUrlTitle: "MeSH Codes URL",
            disableValidation,
            charCountEnabled: true,
            maxChars: 200,
            translations: {
              jp: {
                title: "介入コード",
                tooltip: "こちらを参考に該当のMeSHコードを入力してください",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-2",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        intervention_keywords: {
          "ui:options": {
            label: false,
            title: "Intervention Keywords",
            disableValidation,
            charCountEnabled: true,
            maxChars: 500,
            translations: {
              jp: {
                title: "介入キーワード",
                maxChars: 400,
              },
            },
            classNames: {
              fieldContainer: "rjs-col-1-2",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        primary_outcome_measures: {
          "ui:options": {
            label: false,
            title: "Primary Outcome(s)",
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 8000,
            customFnProps: [
              {
                tooltip: "Combine the values from all the Primary Outcome Measure Title and Timeframe.",
                fnKey: "ctg-oms",
                fnArgs: {
                  omType: ["Primary"],
                  omFields: ["title", "time_frame"],
                },
              },
            ],
            translations: {
              jp: {
                title: "主たる評価項目",
                maxChars: 6000,
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        secondary_outcome_measures: {
          "ui:options": {
            label: false,
            title: "Secondary Outcome(s) ",
            required: true,
            disableValidation,
            charCountEnabled: true,
            maxChars: 8000,
            customFnProps: [
              {
                tooltip: "Combine the values from all the Secondary Outcome Measure Title and Timeframe.",
                fnKey: "ctg-oms",
                fnArgs: {
                  omType: ["Secondary"],
                  omFields: ["title", "time_frame"],
                },
              },
            ],
            translations: {
              jp: {
                title: "副次的な評価項目",
                maxChars: 6000,
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "textarea", preview }),
        },
        study_conditions: {
          ...ctgSchema.protocol.study_description.study_conditions,
          "ui:options": {
            ...ctgSchema.protocol.study_description.study_conditions["ui:options"],
            title: "Health Condition(s) or Problem(s) studied",
          },
        },
        ...setHiddenFields(hideFields.study_description.root),
      },
      study_eligibilty: {
        "ui:options": {
          classNames: {
            fieldContainer: "rjs-full",
            objectContainer: "rjs-gap-16 pb-16",
          },
        },
        "ui:order": ["sex", "*"],
        age_limits: ctgSchema.protocol.study_eligibilty.age_limits,
        sex: {
          "ui:options": {
            title: "Gender",
            label: false,
            required: true,
            disableValidation,
            translations: {
              jp: {
                title: "性別",
              },
            },
            classNames: {
              fieldContainer: "rjs-full",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        ...setHiddenFields(
          [
            ...hideFields.study_eligibilty.root,
            ...Object.keys({
              ...study_eligibilty.interventional.properties,
              ...study_eligibilty.observational.properties,
              ...study_eligibilty.expanded_access.properties,
            }),
          ],
          showFields.study_eligibilty
        ),
      },
      study_scope: {
        "ui:options": {
          classNames: {
            fieldContainer: "rjs-full",
            objectContainer: "rjs-gap-16 pb-16",
          },
        },
        "ui:order": [
          "study_phase",
          "enrollment_number",
          "allocation",
          "jrct_masking",
          "control",
          "interventional_study_model",
          "primary_purpose",
          "placebo",
          "blinding",
          "randomization",
          "non_insurance_combined_medical_expenses",
          "countries_of_recruitment_except_japan",
          "*",
        ],
        study_phase: {
          "ui:options": {
            title: "Phase",
            label: false,
            orientation: "vertical",
            required: true,
            disableValidation,
            translations: {
              jp: {
                title: "試験等のフェーズ",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-sm",
              field: "rjs-col-sm",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        enrollment_number: {
          "ui:options": {
            title: "Number of Subjects",
            required: true,
            disableValidation,
            label: false,
            translations: {
              jp: {
                title: "実施予定被験者数",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-lg",
              field: "rjs-col-sm",
            },
            validations: [
              {
                regex: /^(?!0{2,}$)\d{0,6}$/,
                message: "Acceptable value is from 0 to 999999",
              },
            ],
          },
        },
        allocation: {
          "ui:options": {
            title: "Randomization/Allocation",
            disableValidation,
            label: false,
            required: false,
            translations: {
              jp: {
                title: "無作為化",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-3xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        jrct_masking: {
          "ui:options": {
            title: "jRCT Masking",
            disableValidation,
            label: false,
            translations: {
              jp: {
                title: "盲検化",
              },
            },
            registries: {
              jrct: {
                title: "Masking",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-md",
            },
          },
        },
        control: {
          "ui:options": {
            title: "Control",
            disableValidation,
            label: false,
            translations: {
              jp: {
                title: "対照",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        interventional_study_model: {
          "ui:options": {
            title: "Assignment",
            label: false,
            required: false,
            translations: {
              jp: {
                title: "割付け",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-md",
            },
          },
        },
        primary_purpose: {
          "ui:options": {
            title: "Purpose",
            label: false,
            disableValidation,
            required: false,
            translations: {
              jp: {
                title: "研究目的",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-lg",
            },
          },
        },
        placebo: {
          "ui:options": {
            title: "Placebo",
            label: false,
            disableValidation,
            translations: {
              jp: {
                title: "プラセボの有無",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        blinding: {
          "ui:options": {
            title: "Blinding",
            label: false,
            disableValidation,
            translations: {
              jp: {
                title: "盲検の有無",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        randomization: {
          "ui:options": {
            title: "Randomization",
            label: false,
            disableValidation,
            translations: {
              jp: {
                title: "無作為化の有無",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        non_insurance_combined_medical_expenses: {
          "ui:options": {
            title: "Any non-insurance combined medical expenses?",
            label: false,
            options: [],
            translations: {
              jp: {
                title: "保険外併用療養費の有無",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-xl",
            },
          },
          "ui:widget": getWidget({ type: "radio", preview }),
        },
        countries_of_recruitment_except_japan: {
          "ui:options": {
            title: "Countries of recruitment (except Japan)",
            label: false,
            tooltip: "Please enter None if there are no other countries of recruitment",
            required: true,
            disableValidation,
            translations: {
              jp: {
                title: "実施国(日本以外)",
              },
            },
            classNames: {
              fieldContainer: "rjs-col-4xl",
            },
          },
          "ui:widget": getWidget({ type: "SelectTag", preview }),
        },
        ...setHiddenFields(
          [
            ...hideFields.study_scope.root,
            ...Object.keys({
              ...study_scope.interventional.properties,
              ...study_scope.observational.properties,
              ...study_scope.expanded_access.properties,
            }),
          ],
          showFields.study_scope
        ),
      },
    },
  }

  if (preview === true) {
    getPreviewStyles(returnObj, formData)
  }

  return returnObj
}
